/**
 * useDealAccess(dealId, { pilot } = {})
 * ─────────────────────
 * iter-184 P1 — fetches the caller's effective capability set on a deal
 * from `GET /api/deals/{id}/access` and threads `{visibility, is_org_owner,
 * can_comment, can_upload_documents, can_view_internal_notes}` into the
 * Deal Room surfaces (upload, comment composer, internal notes panel,
 * header avatar rail tooltips).
 *
 * Single canonical hook — never duplicate this lookup in components.
 * Backend is the source of truth; the hook never infers from role
 * strings client-side.
 *
 * Default state (while fetching, or when offline) intentionally returns
 * `null` capability flags so the UI can render the calm muted state
 * until the real value arrives — preventing a flicker of "you can do
 * everything" → "you can't".
 *
 * iter-297 — Pilot bypass. Pilot deals are seeded frontend-only
 * (chainStore) so `GET /api/deals/{id}/access` returns 404 and the
 * legacy "calm degradation" branch flips uploads / comments to
 * read-only. When the caller passes `pilot: true`, the hook short-
 * circuits the API call and returns the canonical "demo write"
 * capability set so the Deal Room dropzone + Upload Document button
 * work end-to-end on demo data.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../data/authStore";

// Same write-role set as the server (routes_persistence.WRITE_DEAL_ROLES).
const WRITE_DEAL_ROLES = new Set(["admin", "director", "manager", "sales_progressor", "senior_sales_negotiator", "sales_negotiator", "negotiator"]);

// Mirrors routes_contacts.require_contact_writer (create / edit / delete).
export const useCanManageContacts = () => WRITE_DEAL_ROLES.has(String(useAuth()?.user?.role || "viewer").toLowerCase());

// Sample (pilot) deals have no server record — derive the same caps the server
// would give an agency member with this role, so viewers stay read-only there too.
const pilotCaps = (role) => {
  const canWrite = WRITE_DEAL_ROLES.has(String(role || "viewer").toLowerCase());
  return {
    ...PILOT_FULL,
    visibility_label: canWrite ? "Demo write" : "Demo read only",
    can_upload_documents: canWrite,
    can_edit_notes: canWrite,
    can_add_participants: canWrite,
  };
};

const PENDING = {
  visibility: null,
  visibility_label: null,
  is_org_owner: false,
  can_comment: null,
  can_upload_documents: null,
  can_view_internal_notes: null,
  loading: true,
  error: null,
};

const PILOT_FULL = {
  visibility: "full",
  visibility_label: "Demo write",
  is_org_owner: true,
  can_comment: true,
  can_upload_documents: true,
  can_view_internal_notes: true,
  loading: false,
  error: null,
};

export const useDealAccess = (dealId, opts = {}) => {
  const pilot = !!opts.pilot;
  const role = useAuth()?.user?.role;
  const [state, setState] = useState(pilot ? pilotCaps(role) : PENDING);

  const refresh = useCallback(async () => {
    if (!dealId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    // iter-297 — Pilot bypass. Demo deals never call the backend
    // access endpoint; the canonical "Demo write" capability set is
    // returned synchronously so the Deal Room renders write
    // affordances correctly from first paint.
    if (pilot) {
      setState(pilotCaps(role));
      return;
    }
    try {
      const { data } = await api.get(`/api/deals/${dealId}/access`);
      setState({
        ...data,
        loading: false,
        error: null,
      });
    } catch (err) {
      // Calm degradation — 4xx/network: assume passive (read_only) so the
      // UI never falsely shows write affordances. Org-owner check is
      // still respected through visibility=read_only path.
      setState({
        visibility: "read_only",
        visibility_label: "Read only",
        is_org_owner: false,
        can_comment: false,
        can_upload_documents: false,
        can_view_internal_notes: false,
        can_edit_notes: false,
        loading: false,
        error: err?.response?.data?.detail || err?.message || "access_unavailable",
      });
    }
  }, [dealId, pilot, role]);

  useEffect(() => {
    let alive = true;
    setState(pilot ? pilotCaps(role) : PENDING);
    (async () => {
      if (!alive) return;
      await refresh();
    })();
    return () => { alive = false; };
  }, [refresh, pilot]);

  return { ...state, refresh };
};

export default useDealAccess;
